import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CompanyInfoCard from './CompanyInfoCard';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { RemoveAdminCompanyDemoDataAPI, SeedAdminCompanyDemoDataAPI } from '../../../../api/AdminAPI';
import { formatDate } from '../../../../utils/Utils';

/** Motivo gravado em companies.demo_removed_reason */
const REASON_LABELS = {
  manual: 'pela conta',
  subscription: 'na assinatura',
  admin: 'pelo Admin Geral',
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(String(dateStr).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? '—' : formatDate(date);
};

const ACTIONS = {
  seed: {
    title: 'Criar dados de exemplo?',
    message: 'A conta recebe pessoas, atendimentos, pagamentos, lista de espera e contatos de exemplo da área. Só vale para conta no período de teste que ainda não recebeu exemplos.',
    confirmLabel: 'Criar exemplos',
    variant: 'info',
    success: 'Dados de exemplo criados.',
  },
  remove: {
    title: 'Remover dados de exemplo?',
    message: 'Tudo o que é exemplo sai da conta, inclusive o que foi registrado nas pessoas de exemplo. Os dados reais ficam. Não dá para desfazer.',
    confirmLabel: 'Remover exemplos',
    variant: 'danger',
    success: 'Dados de exemplo removidos.',
  },
};

/**
 * CompanyDemoDataCard - Dados de exemplo da conta na ficha do Admin Geral
 * (docs/features/trial-demo-data/README.md, seção 8): quando foram criados,
 * quando e por que foram removidos, e as ações de criar (conta em teste sem
 * exemplo) e remover (motivo "admin"), cada uma com confirmação.
 * @param {Object} props
 * @param {number} props.companyId
 * @param {Object|null} props.demoData { seeded_at, removed_at, reason } de admin/companies/get.php
 * @param {Function} props.onToast (message, severity)
 * @param {Function} props.onChanged recarrega a empresa
 */
function CompanyDemoDataCard({ companyId, demoData, onToast, onChanged }) {
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const seededAt = demoData?.seeded_at || null;
  const removedAt = demoData?.removed_at || null;
  const active = !!seededAt && !removedAt;

  let state = 'Nunca criados';
  if (active) state = 'Na conta';
  else if (removedAt) state = 'Removidos';

  const rows = [
    { label: 'Situação', value: state },
    seededAt && { label: 'Criados em', value: fmtDate(seededAt) },
    removedAt && {
      label: 'Removidos em',
      value: `${fmtDate(removedAt)}${REASON_LABELS[demoData.reason] ? ` (${REASON_LABELS[demoData.reason]})` : ''}`,
    },
  ].filter(Boolean);

  const confirm = () => {
    const action = ACTIONS[pending];
    const call = pending === 'seed' ? SeedAdminCompanyDemoDataAPI : RemoveAdminCompanyDemoDataAPI;
    setSaving(true);
    call(companyId, (response) => {
      setSaving(false);
      setPending(null);
      if (!response || response.success === false) {
        onToast(response?.error || 'Não foi possível concluir. Tente novamente.', 'error');
        return;
      }
      onToast(action.success, 'success');
      onChanged();
    });
  };

  const footer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {!seededAt && (
        <>
          <Typography variant="caption" color="text.secondary">
            Para contas abertas antes dos exemplos. Só cria em conta no período de teste.
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setPending('seed')} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
            Criar dados de exemplo
          </Button>
        </>
      )}
      {active && (
        <Button size="small" variant="outlined" color="error" onClick={() => setPending('remove')} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
          Remover dados de exemplo
        </Button>
      )}
    </Box>
  );

  const action = pending ? ACTIONS[pending] : null;

  return (
    <>
      <CompanyInfoCard
        icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
        title="Dados de exemplo"
        rows={rows}
        footer={removedAt ? null : footer}
      />
      <ConfirmDialog
        isOpen={!!action}
        title={action?.title || ''}
        message={action?.message}
        confirmLabel={action?.confirmLabel}
        variant={action?.variant}
        loading={saving}
        onConfirm={confirm}
        onCancel={() => { if (!saving) setPending(null); }}
      />
    </>
  );
}

export default CompanyDemoDataCard;
